/* eslint-disable prefer-promise-reject-errors */
/* global google */
import loadScript from 'load-script';
import Cookie from 'js-cookie';
import { promisify } from '../promise';
import * as C from './constantes';

/**
  * [PUBLIC] initGoogleMapScript - insert Google Map script if not presents yet.
  * @param {function} callback -
  * @return null
  * @example initGoogleMapScript(() => { ... });
  */

const initGoogleMapScript = (callback) => {
  let scriptLoading = false;
  const queue = [];
  if (window.google && window.google.maps) {
    if (typeof callback === 'function') {
      callback();
    }
  } else if (scriptLoading === false) {
    scriptLoading = true;
    const loadGScript = promisify(loadScript);
    loadGScript(`https://maps.google.com/maps/api/js?client=${C.GOOGLE_API_KEY}&v=3.28&libraries=places`)
      .then(() => {
        if (typeof callback === 'function') {
          callback();
        }
        const queueLen = queue.length;
        if (queueLen > 0) {
          for (let i = 0; i < queueLen; i += 1) {
            queue[i]();
          }
        }
      });
  } else {
    queue.push(callback);
  }
};

/**
  * [PUBLIC] canIGeoloc - Check if the user position is available through the browser API.
  *
  * @method
  * @returns { Promise } A promise that returns { the user position } if resolved
  * and { an error } if rejected.
  */
const canIGeoloc = () => (
  new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        resolve(position);
      }, err => reject(err));
    } else {
      reject({ message: 'Geolocation feature is not available on your browser.' });
    }
  })
);

/**
  * [PUBLIC] getGeolocCookie - Get the geoloc cookie based on the constant name.
  *
  * @method
  * @returns { Promise } A promise that returns the geolocation { cookie }
  * in JSON format if resolved and { null } if rejected.
  */
const getGeolocCookie = () => (
  new Promise((resolve, reject) => {
    const cookie = Cookie.get(C.COOKIE_NAME);
    if (cookie) {
      const decoded = decodeURIComponent(cookie);
      try {
        const json = JSON.parse(decoded);
        return resolve(json);
      } catch (err) {
        return reject({ message: 'Geolocation cookie not in JSON format' });
      }
    }

    return reject({ message: 'No Geolocation cookie available' });
  })
);

/**
  * [PUBLIC] initMap - Initialiazed a Google Map based on a DIV ID
  * @method
  * @param { JSON } options [optional]- Geocode and Google Map options
  * @returns { Promise } A promise that returns { the Google Map instance } if resolved
  * and { an error } if rejected.
  */
const initMap = (containerId, options = {}) => (
  new Promise((resolve, reject) => {
    if (window.google) {
      const container = document.getElementById(containerId);
      if (container) {
        const localizationMap = new google.maps.Map(container.querySelector(`#${containerId}_map`), {
          center: {
            lat: options.geocode ? options.geocode.lat : C.DEFAULT_LAT,
            lng: options.geocode ? options.geocode.lng : C.DEFAULT_LNG,
          },
          scaleControl: true,
          zoomControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullScreenControl: false,
          zoom: options.zoom || C.DEFAULT_ZOOM,
        });
        if (typeof options.onzoom === 'function') {
          localizationMap.addListener('zoom_changed', options.onzoom);
        }
        return resolve(localizationMap);
      }
      return reject({ message: 'No DOM container available to instanciate a Google Map.' });
    }
    return reject({ message: 'No Google API initialized' });
  })
);

/**
  * [PUBLIC] setGeolocCookie - Save/Update the geolocation cookie based
  * on the constant name and the received value.
  * @method
  * @param { JSON } placeDetails - Full place details
  * @returns null
  */
const setGeolocCookie = (placeDetails) => {
  const geoCookieValue = {
    locality: placeDetails.locality || '',
    street_number: placeDetails.street_number,
    zipcode: placeDetails.postal_code,
    route: placeDetails.route,
    description: placeDetails.description || '',
    geocode: {
      lat: placeDetails.geocode ? placeDetails.geocode.lat : null,
      lng: placeDetails.geocode ? placeDetails.geocode.lng : null,
    },
    radius: placeDetails.radius,
    zoom: placeDetails.zoom,
  };
  Cookie.set(C.COOKIE_NAME, geoCookieValue, { expires: 365 });
};

/**
  * [PUBLIC] getGoogleMapSuggestion - Get the addresses suggestion
  * based on an input String, through the Google Map API.
  * @method
  * @param { String } value - Geolocation Cookie value
  * @param { JSON } options [optional] - Geolocation Cookie value
  * @returns { Promise } A promise that returns { an array of places (objects) } if resolved
  * and { an error } if rejected.
  */
const getGoogleMapSuggestion = (value, options = { ctryRestr: 'fr' }) => (
  new Promise((resolve, reject) => {
    if (!window.google) {
      return reject({ message: 'No Google API initialized.' });
    }
    if (!value) {
      return reject({ message: 'No value received.' });
    }
    if (value !== '') {
      return new google.maps.places.AutocompleteService().getPlacePredictions({
        input: value,
        componentRestrictions: { country: options.ctryRestr },
      }, (places, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          return resolve({ data: places });
        }
        return resolve({ data: [] });
      });
    }
    return resolve({ data: [] });
  })
);

/**
  * [PUBLIC] getPlaceDetails - Get a place details
  * based on an place ID, through the Google Map API.
  * @method
  * @param { String } placeId - Google MAP Place ID
  * @returns { Promise } A promise that returns { a place details (coords and address) } if resolved
  * and { an error } if rejected.
  */
const getPlaceDetails = placeId => (
  new Promise((resolve, reject) => {
    const mapEl = document.createElement('DIV');
    const placeService = new google.maps.places.PlacesService(mapEl);
    const place = {};
    placeService.getDetails({ placeId }, (placeDetails) => {
      const len = placeDetails.address_components.length;
      const address = placeDetails.address_components;
      for (let i = 0; i < len; i += 1) {
        place[address[i].types[0]] = address[i].long_name;
      }
      place.geocode = {
        lat: placeDetails.geometry.location.lat(),
        lng: placeDetails.geometry.location.lng(),
      };
      resolve(place);
    }, err => reject(err));
  })
);

/**
  * [PUBLIC] getGoogleMapZoom - Get Google Map's zoom level.
  * @method
  * @param { Object } map - Google MAP instance.
  * @returns { Promise } A promise that returns { the zoom level } if resolved
  * and { an error } if rejected.
  */
const getGoogleMapZoom = map => (
  new Promise((resolve, reject) => {
    if (!window.google) {
      return reject({ message: 'No Google API initialized.' });
    }
    if (map) {
      const zoom = map.getZoom();
      return resolve(zoom);
    }
    return reject({ message: 'No Map object type received.' });
  })
);

/**
  * [PUBLIC] setGeolocMarker - Add a Geolocation marker on the map.
  * @method
  * @param { Object } map - Google MAP instance.
  * @param { JSON } geocode [optional] - latitude and longitude of the marker.
  * @returns { Promise } A promise that returns { null } if resolved
  * and { an error } if rejected.
  */
const setGeolocMarker = (
  map,
  geocode = { lat: C.DEFAULT_LAT, lng: C.DEFAULT_LNG },
  radius = C.DEFAULT_RADIUS,
  options = { icon: C.USER_GEOLOC_MARKER },
) => (
  new Promise((resolve, reject) => {
    if (!window.google) {
      return reject({ message: 'No Google API initialized.' });
    }
    if (!map) {
      return reject({ message: 'No map available.' });
    }
    const circle = new google.maps.Circle({
      center: geocode,
      radius: radius * 1000, // in meter
      strokeColor: C.CIRCLE_STROKE_COLOR,
      strokeOpacity: C.CIRCLE_STROKE_OPACITY,
      strokeWeight: C.CIRCLE_STROKE_WEIGHT,
      fillColor: C.CIRCLE_FILL_COLOR,
      fillOpacity: C.CIRCLE_FILL_OPACITY,
      map,
    });
    const marker = new google.maps.Marker({
      position: geocode,
      map,
      icon: options.icon,
      title: options.title || '',
    });
    map.setCenter(geocode);
    return resolve({ marker, circle });
  })
);

/**
  * [PUBLIC] setGeolocCircleRadius - Set the radius of
  * a Google Map Circle instance
  * @method
  * @param { object } map - Google Map instance
  * @param { number } radius - Google Map Circle instance
  * @returns { Promise } A promise that returns { the set circle } if resolved
  * and { an error } if rejected.
  */
const setGeolocCircleRadius = (
  circle,
  radius = C.DEFAULT_RADIUS,
) => (
  new Promise((resolve, reject) => {
    if (circle && typeof radius === 'number') {
      circle.setRadius(radius * 1000);
      return resolve(circle);
    }
    return reject({ message: 'Missing value or wrong type.' });
  })
);

/**
  * [PUBLIC] fitGeolocCircle - Adapt the zoom level to the radius bounds.
  * @method
  * @param { Object } map - Google Map instance
  * @param { Object } circle - Google Map Circle instance
  * @returns { Promise } A promise that returns { null } if resolved
  * and { an error } if rejected.
  */
const fitGeolocCircle = (map, circle) => (
  new Promise((resolve, reject) => {
    if (map && circle) {
      map.fitBounds(circle.getBounds());
      return resolve();
    }
    return reject({ message: 'Missing values Map and/or Circle.' });
  })
);

/**
  * [PUBLIC] getPlaceIdFromCoords - Get the Google Map Place ID
  * from the geocode coordinates.
  * @method
  * @param { JSON } coords - Coordinates ( { lat: ..., lng: ...} )
  * @returns { Promise } A promise that returns { the place ID) } if resolved
  * and { an error } if rejected.
  */
const getPlaceIdFromCoords = coords => (
  new Promise((resolve, reject) => {
    if (!window.google) {
      return reject({ message: 'No Google API initialized.' });
    }
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: coords }, (res, status) => {
      if ((status === google.maps.GeocoderStatus.OK) && res[1]) {
        return resolve(res[1].place_id);
      }
      return reject({ message: 'No result found.' });
    });
    return false;
  })
);

export { getGeolocCookie, canIGeoloc, setGeolocCookie, initGoogleMapScript,
  initMap, getGoogleMapSuggestion, setGeolocMarker, getPlaceDetails, getPlaceIdFromCoords,
  setGeolocCircleRadius, getGoogleMapZoom, fitGeolocCircle };
